<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { useResourcesStore } from '@/stores/resources'
import { useTr } from '@/composables/useTr'

const app = useAppStore()
const res = useResourcesStore()
const router = useRouter()
const { tr, locale } = useTr()

// === New scan modal state =================================================
const scanFormTargetId = ref<number | null>(null)
const scanFormPreset = ref<'public_recon' | 'standard_sweep' | 'deep_audit'>('public_recon')
const scanFormSubmitting = ref(false)
const scanFormError = ref<string | null>(null)
const scanFormSuccessMsg = ref<string | null>(null)

watch(
  () => app.newScanModal,
  (open) => {
    if (!open) return
    // Lazy-load targets so the dropdown has options the first time it's opened
    res.fetchTargets()
    res.fetchScans()
    scanFormError.value = null
    scanFormSuccessMsg.value = null
    if (app.newScanPrefillTargetId) {
      scanFormTargetId.value = app.newScanPrefillTargetId
    } else if (scanFormTargetId.value === null && res.targets.items.length > 0) {
      scanFormTargetId.value = res.targets.items[0].id
    }
  },
)

async function submitScan() {
  if (!scanFormTargetId.value) {
    scanFormError.value = tr('请先选择一个目标', 'Please pick a target first')
    return
  }
  scanFormSubmitting.value = true
  scanFormError.value = null
  scanFormSuccessMsg.value = null
  try {
    const job = await res.startScan(scanFormTargetId.value, scanFormPreset.value)
    scanFormSuccessMsg.value = tr(
      `已入队 (扫描 #${job.id}) — 进度会出现在工作台顶部`,
      `Queued (scan #${job.id}) — progress will appear at the top of the Workbench`,
    )
  } catch (err: unknown) {
    const detail = err instanceof Error ? err.message : String(err)
    scanFormError.value = tr(
      `提交失败: ${detail}。请确认后端 / Worker 正常运行。`,
      `Submit failed: ${detail}. Make sure the backend + worker are running.`,
    )
  } finally {
    scanFormSubmitting.value = false
  }
}

function goRebuttalFromContact() {
  app.contactModal = false
  app.aiTab = 'rebuttal'
  router.push({ name: 'ai-triage' })
}

// === Report preview modal helpers =========================================
const previewMarkdown = computed(() => {
  if (app.generatedReport) return app.generatedReport.markdown
  return tr(
    '尚未生成报告。请在「Reports → 报告模板」页面选择 finding + 平台 + 语言,点「生成报告」。',
    'No report generated yet. Pick a finding + platform + language on the Reports → Templates page and click Generate Report.',
  )
})
const previewTitle = computed(() => app.generatedReport?.title ?? tr('可提交报告预览', 'Submittable Report Preview'))
const previewSummary = computed(() => {
  if (!app.generatedReport) return ''
  const r = app.generatedReport
  const lang = r.language === 'en' ? 'English' : r.language === 'bilingual' ? tr('双语', 'Bilingual') : tr('中文', 'Chinese')
  return tr(
    `字符数 ${r.charCount.toLocaleString()} · 平台 ${r.platform} · 语言 ${lang}`,
    `${r.charCount.toLocaleString()} chars · ${r.platform} · ${lang}`,
  )
})

async function copyReport() {
  if (!app.generatedReport) return
  try {
    await navigator.clipboard.writeText(app.generatedReport.markdown)
  } catch {
    /* clipboard blocked (insecure context); fall through silently */
  }
}

function downloadReport(format: 'md' | 'json') {
  if (!app.generatedReport) return
  const safeTitle = app.generatedReport.title.replace(/[^\p{L}\p{N}_-]+/gu, '_').slice(0, 60) || 'report'
  let content: string
  let mime: string
  let ext: string
  if (format === 'json') {
    content = JSON.stringify(app.generatedReport, null, 2)
    mime = 'application/json'
    ext = 'json'
  } else {
    content = app.generatedReport.markdown
    mime = 'text/markdown'
    ext = 'md'
  }
  const blob = new Blob([content], { type: `${mime};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${safeTitle}.${ext}`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
</script>

<template>
  <!-- Pro Modal -->
  <div class="modal-mask" :class="{ shown: app.proModal }" @click.self="app.proModal = false">
    <div class="card" style="max-width:520px; padding:28px; width:90vw; border:1px solid var(--line);">
      <div class="row-between" style="margin-bottom:18px;">
        <div>
          <div class="chip chip-soft" style="margin-bottom:8px;">{{ tr('PRO · 即将上线', 'PRO · Coming Soon') }}</div>
          <h2>{{ tr('升级到 Pro', 'Upgrade to Pro') }}</h2>
          <div class="muted" style="font-size:12.5px; margin-top:4px;">{{ tr('让线索快速变成可交付的报告 · 节省您的时间', 'Turn findings into deliverable reports fast · save your time') }}</div>
        </div>
        <button class="icon-btn" @click="app.proModal = false">×</button>
      </div>

      <div class="card" style="background:var(--panel-soft); margin-bottom:16px; padding:18px;">
        <div class="row" style="align-items:baseline; gap:6px;">
          <span style="font-size:28px; font-weight:700; letter-spacing:-0.02em;">$20</span>
          <span class="muted">{{ tr('/ 月', '/ month') }}</span>
          <span class="chip chip-soft" style="margin-left:auto;">{{ tr('早鸟 8 折 $16', 'Early Bird 20% off · $16') }}</span>
        </div>
        <div class="faint" style="font-size:11.5px; margin-top:6px;">{{ tr('USDT 支付 · 无需信用卡 · 上线前预订锁价 12 个月', 'USDT payment · no credit card · 12-month price lock on pre-order') }}</div>
      </div>

      <h4 style="margin-bottom:10px;">{{ tr('Pro 解锁能力', 'Pro Features') }}</h4>
      <div style="display:flex; flex-direction:column; gap:8px; font-size:13px; margin-bottom:18px;">
        <div class="row" style="gap:8px; align-items:flex-start;">
          <svg width="14" height="14" fill="none" stroke="var(--text-dim)" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0; margin-top:3px;"><polyline points="20 6 9 17 4 12"/></svg>
          <div>
            <span style="font-weight:500;">{{ tr('报告草稿生成', 'Report Draft Generation') }}</span>
            <span class="faint" style="font-size:11.5px;">{{ tr(' — 严格对齐 HackerOne / Bugcrowd / Intigriti / YesWeHack / 漏洞盒子 / 补天 等 Web 资产赏金平台模板规范', ' — Strict alignment with web-asset bounty platform templates: HackerOne / Bugcrowd / Intigriti / YesWeHack / Vulbox / Butian') }}</span>
          </div>
        </div>
        <div class="row" style="gap:8px; align-items:flex-start;">
          <svg width="14" height="14" fill="none" stroke="var(--text-dim)" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0; margin-top:3px;"><polyline points="20 6 9 17 4 12"/></svg>
          <div>
            <span style="font-weight:500;">{{ tr('公开记录查重与赏金区间估算', 'Public-Record Dedup & Bounty Range Estimation') }}</span>
            <span class="faint" style="font-size:11.5px;">{{ tr(' — 检索 GitHub / 厂商公告 / 审计报告与历史披露,辅助评估申报优先级', ' — Searches GitHub / vendor advisories / audit reports / historical disclosures to inform submission priority') }}</span>
          </div>
        </div>
        <div class="row" style="gap:8px; align-items:flex-start;">
          <svg width="14" height="14" fill="none" stroke="var(--text-dim)" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0; margin-top:3px;"><polyline points="20 6 9 17 4 12"/></svg>
          <div>
            <span style="font-weight:500;">{{ tr('降级判定申诉文本辅助', 'Downgrade Appeal Drafting') }}</span>
            <span class="faint" style="font-size:11.5px;">{{ tr(' — 引用平台严重度规则、CVSS 推导、KEV 命中与公开案例先例生成结构化申诉文本', ' — Generates structured appeal text citing platform severity rules, CVSS derivation, KEV hits, and public precedents') }}</span>
          </div>
        </div>
        <div class="row" style="gap:8px; align-items:flex-start;">
          <svg width="14" height="14" fill="none" stroke="var(--text-dim)" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0; margin-top:3px;"><polyline points="20 6 9 17 4 12"/></svg>
          <div>
            <span style="font-weight:500;">{{ tr('扩展验证路径调度执行', 'Extended Validation Path Dispatch') }}</span>
            <span class="faint" style="font-size:11.5px;">{{ tr(' — AI 生成的扩展验证命令一键调度至 worker,结果回写至 Findings', ' — AI-generated extended validation commands dispatched to worker with one click; results written back to Findings') }}</span>
          </div>
        </div>
        <div class="row" style="gap:8px; align-items:flex-start;">
          <svg width="14" height="14" fill="none" stroke="var(--text-dim)" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0; margin-top:3px;"><polyline points="20 6 9 17 4 12"/></svg>
          <div>
            <span style="font-weight:500;">{{ tr('跨发现关联与合并报告生成', 'Cross-Finding Correlation & Merged Report Generation') }}</span>
            <span class="faint" style="font-size:11.5px;">{{ tr(' — 识别可组合为高危的低危发现组合,自动生成合并报告草稿', ' — Identifies low-severity combinations that aggregate to critical; auto-generates merged report draft') }}</span>
          </div>
        </div>
        <div class="row" style="gap:8px; align-items:flex-start;">
          <svg width="14" height="14" fill="none" stroke="var(--text-dim)" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0; margin-top:3px;"><polyline points="20 6 9 17 4 12"/></svg>
          <div>
            <span style="font-weight:500;">{{ tr('资产与发现持续监控', 'Continuous Asset & Finding Monitoring') }}</span>
            <span class="faint" style="font-size:11.5px;">{{ tr(' — 定时增量扫描与变化推送 (飞书 / Telegram / 邮件 / Webhook),即将上线', ' — Scheduled incremental scans with change notifications (Feishu / Telegram / email / webhook). Coming soon.') }}</span>
          </div>
        </div>
      </div>

      <div style="padding:12px 14px; background:var(--panel-soft); border:1px solid var(--line-soft); border-radius:8px; margin-bottom:16px;">
        <div class="row" style="gap:10px; align-items:flex-start;">
          <svg width="16" height="16" fill="none" stroke="var(--text)" stroke-width="2" viewBox="0 0 24 24" style="flex-shrink:0; margin-top:1px;"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <div style="font-size:12px;">
            <div style="font-weight:600; margin-bottom:4px;">{{ tr('数据主权完全在您手里', 'Your data, your control') }}</div>
            <div class="muted" style="line-height:1.65;">{{ tr('所有 AI 调用均使用您绑定的 API Key, 数据从浏览器直连模型服务商, 不经过平台服务器。扫描内容、线索与报告全程仅保存于您本地。', 'All AI calls use your bound API Key, data goes from browser direct to vendor, never through platform servers. Scan content, findings, and reports stay on your local machine.') }}</div>
          </div>
        </div>
      </div>

      <div class="row" style="gap:8px;">
        <button class="btn btn-primary btn-sm" style="flex:1; justify-content:center; padding:9px;">{{ tr('查看定价 →', 'View Pricing →') }}</button>
        <button class="btn btn-ghost btn-sm" @click="app.proModal = false">{{ tr('关闭', 'Close') }}</button>
      </div>
    </div>
  </div>

  <!-- Developer API Modal -->
  <div class="modal-mask" :class="{ shown: app.devApiModal }" @click.self="app.devApiModal = false">
    <div class="card" style="max-width:600px; padding:24px; width:90vw; border:1px solid var(--line);">
      <div class="row-between" style="margin-bottom:16px;">
        <div>
          <div class="chip chip-soft" style="margin-bottom:6px;">BETA</div>
          <h2>{{ tr('开发者接口', 'Developer API') }}</h2>
          <div class="muted" style="font-size:12.5px; margin-top:3px;">{{ tr('提供代码级接口, 可用于扫描控制、数据拉取与 CI 集成', 'Programmatic API for scan control, data fetch, and CI integration') }}</div>
        </div>
        <button class="icon-btn" @click="app.devApiModal = false">×</button>
      </div>

      <h4 style="margin-bottom:8px; font-size:12px; text-transform:uppercase; color:var(--text-faint); letter-spacing:0.06em;">{{ tr('您的 API Token', 'Your API Token') }}</h4>
      <div class="row" style="gap:6px; margin-bottom:14px;">
        <div class="mono" style="flex:1; padding:8px 12px; background:var(--panel-soft); border:1px solid var(--line-soft); border-radius:6px; font-size:12.5px;">gmcn_•••••••••••••••••••••a3f8</div>
        <button class="btn btn-ghost btn-sm">{{ tr('复制', 'Copy') }}</button>
        <button class="btn btn-ghost btn-sm">{{ tr('重新生成', 'Regenerate') }}</button>
      </div>

      <h4 style="margin-bottom:8px; font-size:12px; text-transform:uppercase; color:var(--text-faint); letter-spacing:0.06em;">Base URL</h4>
      <div class="mono" style="padding:8px 12px; background:var(--panel-soft); border:1px solid var(--line-soft); border-radius:6px; font-size:12.5px; margin-bottom:18px;">https://api.ghostmap.cn/v1</div>

      <h4 style="margin-bottom:10px; font-size:12px; text-transform:uppercase; color:var(--text-faint); letter-spacing:0.06em;">{{ tr('示例命令', 'Example Commands') }}</h4>
<pre class="card" style="background:var(--panel-soft); padding:14px; margin-bottom:14px; font-size:11.5px; line-height:1.65; color:var(--text-dim); white-space:pre-wrap; font-family:ui-monospace,JetBrains Mono,monospace;">$ curl -H "Authorization: Bearer $TOKEN" \
    https://api.ghostmap.cn/v1/targets

$ curl -H "Authorization: Bearer $TOKEN" \
    "https://api.ghostmap.cn/v1/findings?sev=crit"

$ curl -X POST -H "Authorization: Bearer $TOKEN" \
    https://api.ghostmap.cn/v1/scans -d '{"target":"...","preset":"deep"}'</pre>

      <div class="row" style="gap:8px;">
        <button class="btn btn-primary btn-sm" style="flex:1; justify-content:center;">{{ tr('完整文档 (Swagger) →', 'Full Docs (Swagger) →') }}</button>
        <button class="btn btn-ghost btn-sm" @click="app.devApiModal = false">{{ tr('关闭', 'Close') }}</button>
      </div>
    </div>
  </div>

  <!-- Subscription Modal -->
  <div class="modal-mask" :class="{ shown: app.subscriptionModal }" @click.self="app.subscriptionModal = false">
    <div class="card" style="max-width:440px; padding:24px; width:90vw; border:1px solid var(--line);">
      <div class="row-between" style="margin-bottom:16px;">
        <div>
          <h2>{{ tr('订阅状态', 'Subscription Status') }}</h2>
          <div class="muted" style="font-size:12.5px; margin-top:3px;">{{ tr('查看当前订阅套餐与本月用量', 'View your current plan and monthly usage') }}</div>
        </div>
        <button class="icon-btn" @click="app.subscriptionModal = false">×</button>
      </div>

      <div class="card" style="background:var(--panel-soft); padding:16px; margin-bottom:14px;">
        <div class="row-between" style="margin-bottom:6px;">
          <div style="font-weight:600;">{{ app.isPro ? tr('Pro · 早鸟版', 'Pro · Early Bird') : tr('免费版', 'Free Edition') }}</div>
          <span class="chip chip-soft">{{ app.isPro ? tr('到期 2027-05', 'Expires 2027-05') : tr('永久免费', 'Free Forever') }}</span>
        </div>
        <div class="faint" style="font-size:11.5px;">{{ app.isPro ? tr('$16/月 · 锁价 12 个月', '$16/mo · locked 12 months') : tr('包含基础侦察、安全检查与通知功能。如需 AI 报告草稿能力, 可升级至 Pro', 'Includes recon, checks, and notifications. Upgrade to Pro for AI report drafts') }}</div>
      </div>

      <h4 style="margin-bottom:10px; font-size:12px; text-transform:uppercase; color:var(--text-faint); letter-spacing:0.06em;">{{ tr("本月用量", "This Month's Usage") }}</h4>
      <div style="display:flex; flex-direction:column; gap:8px; font-size:13px; margin-bottom:16px;">
        <div class="row-between"><span class="muted">{{ tr('扫描目标', 'Scan Targets') }}</span><span class="num">5 / 5</span></div>
        <div class="row-between"><span class="muted">{{ tr('资产入库', 'Assets Stored') }}</span><span class="num">2,847 / 10,000</span></div>
        <div class="row-between"><span class="muted">{{ tr('线索数', 'Findings') }}</span><span class="num">248 / 1,000</span></div>
        <div class="row-between"><span class="muted">{{ tr('导出报告', 'Report Exports') }}</span><span class="num">8 / 30</span></div>
      </div>

      <div class="row" style="gap:8px;">
        <button v-show="!app.isPro" class="btn btn-primary btn-sm" style="flex:1; justify-content:center;" @click="app.subscriptionModal = false; app.proModal = true">{{ tr('升级到 Pro →', 'Upgrade to Pro →') }}</button>
        <button v-show="app.isPro" class="btn btn-primary btn-sm" style="flex:1; justify-content:center;">{{ tr('续费', 'Renew') }}</button>
        <button class="btn btn-ghost btn-sm" @click="app.subscriptionModal = false">{{ tr('关闭', 'Close') }}</button>
      </div>
    </div>
  </div>

  <!-- System Status Modal -->
  <div class="modal-mask" :class="{ shown: app.sysStatusModal }" @click.self="app.sysStatusModal = false">
    <div class="card" style="max-width:480px; padding:24px; width:90vw; border:1px solid var(--line);">
      <div class="row-between" style="margin-bottom:16px;">
        <div>
          <div class="row" style="gap:8px;">
            <span style="display:inline-block; width:8px; height:8px; border-radius:50%; background:var(--sev-low);" />
            <h2 style="margin:0;">{{ tr('系统状态', 'System Status') }}</h2>
          </div>
          <div class="muted" style="font-size:12.5px; margin-top:3px;">{{ tr('所有服务正常', 'All services operational') }}</div>
        </div>
        <button class="icon-btn" @click="app.sysStatusModal = false">×</button>
      </div>

      <div style="display:flex; flex-direction:column; gap:8px; font-size:13px; margin-bottom:14px;">
        <div class="row-between" style="padding:10px 12px; background:var(--panel-soft); border-radius:6px;">
          <span>{{ tr('API 网关', 'API Gateway') }}</span>
          <div class="row" style="gap:6px;"><span class="chip chip-soft" style="background:rgba(77,155,110,0.15); color:var(--sev-low);">{{ tr('正常', 'Healthy') }}</span><span class="muted num" style="font-size:11.5px;">12ms</span></div>
        </div>
        <div class="row-between" style="padding:10px 12px; background:var(--panel-soft); border-radius:6px;">
          <span>{{ tr('扫描 Worker', 'Scan Worker') }}</span>
          <div class="row" style="gap:6px;"><span class="chip chip-soft" style="background:rgba(77,155,110,0.15); color:var(--sev-low);">{{ tr('正常', 'Healthy') }}</span><span class="muted num" style="font-size:11.5px;">{{ tr('3 节点', '3 nodes') }}</span></div>
        </div>
        <div class="row-between" style="padding:10px 12px; background:var(--panel-soft); border-radius:6px;">
          <span>{{ tr('数据库', 'Database') }}</span>
          <div class="row" style="gap:6px;"><span class="chip chip-soft" style="background:rgba(77,155,110,0.15); color:var(--sev-low);">{{ tr('正常', 'Healthy') }}</span><span class="muted num" style="font-size:11.5px;">8ms</span></div>
        </div>
        <div class="row-between" style="padding:10px 12px; background:var(--panel-soft); border-radius:6px;">
          <span>{{ tr('Redis 队列', 'Redis Queue') }}</span>
          <div class="row" style="gap:6px;"><span class="chip chip-soft" style="background:rgba(77,155,110,0.15); color:var(--sev-low);">{{ tr('正常', 'Healthy') }}</span><span class="muted num" style="font-size:11.5px;">{{ tr('2 排队', '2 queued') }}</span></div>
        </div>
      </div>

      <div class="row" style="gap:8px;">
        <button class="btn btn-ghost btn-sm" style="flex:1; justify-content:center;">{{ tr('完整状态页 →', 'Full Status Page →') }}</button>
        <button class="btn btn-ghost btn-sm" @click="app.sysStatusModal = false">{{ tr('关闭', 'Close') }}</button>
      </div>
    </div>
  </div>

  <!-- Audit Log Modal -->
  <div class="modal-mask" :class="{ shown: app.auditLogModal }" @click.self="app.auditLogModal = false">
    <div class="card" style="max-width:560px; padding:24px; width:90vw; border:1px solid var(--line);">
      <div class="row-between" style="margin-bottom:16px;">
        <div>
          <h2>{{ tr('操作日志', 'Audit Log') }}</h2>
          <div class="muted" style="font-size:12.5px; margin-top:3px;">{{ tr('您账号的近期操作记录', 'Recent activity on your account') }}</div>
        </div>
        <button class="icon-btn" @click="app.auditLogModal = false">×</button>
      </div>

      <div style="display:flex; flex-direction:column; gap:0; margin-bottom:14px;">
        <div class="row-between" style="padding:10px 0; border-bottom:1px solid var(--line-soft); font-size:13px;">
          <div style="min-width:0; flex:1;">
            <div style="font-weight:500;">{{ tr('登录', 'Sign In') }}</div>
            <div class="faint mono" style="font-size:11px; margin-top:2px;">IP 52.84.x.x · Chrome 130 · macOS</div>
          </div>
          <span class="muted num" style="font-size:11.5px; flex-shrink:0; margin-left:10px;">{{ tr('5 分钟前', '5 min ago') }}</span>
        </div>
        <div class="row-between" style="padding:10px 0; border-bottom:1px solid var(--line-soft); font-size:13px;">
          <div style="min-width:0; flex:1;">
            <div style="font-weight:500;">{{ tr('新建扫描目标', 'New Scan Target') }}</div>
            <div class="faint mono" style="font-size:11px; margin-top:2px;">*.acme.example.com</div>
          </div>
          <span class="muted num" style="font-size:11.5px; flex-shrink:0; margin-left:10px;">{{ tr('1 小时前', '1 hour ago') }}</span>
        </div>
        <div class="row-between" style="padding:10px 0; border-bottom:1px solid var(--line-soft); font-size:13px;">
          <div style="min-width:0; flex:1;">
            <div style="font-weight:500;">{{ tr('修改 AI 模型设置', 'Update AI Model Settings') }}</div>
            <div class="faint mono" style="font-size:11px; margin-top:2px;">{{ tr('添加 DeepSeek · 默认改为 Qwen3', 'Added DeepSeek · default changed to Qwen3') }}</div>
          </div>
          <span class="muted num" style="font-size:11.5px; flex-shrink:0; margin-left:10px;">{{ tr('3 小时前', '3 hours ago') }}</span>
        </div>
        <div class="row-between" style="padding:10px 0; border-bottom:1px solid var(--line-soft); font-size:13px;">
          <div style="min-width:0; flex:1;">
            <div style="font-weight:500;">{{ tr('导出报告', 'Export Report') }}</div>
            <div class="faint mono" style="font-size:11px; margin-top:2px;">{{ tr('findings_q1_2026.pdf · 248 条', 'findings_q1_2026.pdf · 248 entries') }}</div>
          </div>
          <span class="muted num" style="font-size:11.5px; flex-shrink:0; margin-left:10px;">{{ tr('昨天 23:12', 'Yesterday 23:12') }}</span>
        </div>
        <div class="row-between" style="padding:10px 0; font-size:13px;">
          <div style="min-width:0; flex:1;">
            <div style="font-weight:500;">{{ tr('绑定 Telegram 通知', 'Bind Telegram Notifications') }}</div>
            <div class="faint mono" style="font-size:11px; margin-top:2px;">@your_telegram</div>
          </div>
          <span class="muted num" style="font-size:11.5px; flex-shrink:0; margin-left:10px;">{{ tr('2 天前', '2 days ago') }}</span>
        </div>
      </div>

      <div class="row" style="gap:8px;">
        <button class="btn btn-ghost btn-sm" style="flex:1; justify-content:center;">{{ tr('查看全部 →', 'View All →') }}</button>
        <button class="btn btn-ghost btn-sm" @click="app.auditLogModal = false">{{ tr('关闭', 'Close') }}</button>
      </div>
    </div>
  </div>

  <!-- Contact Modal -->
  <div class="modal-mask" :class="{ shown: app.contactModal }" @click.self="app.contactModal = false">
    <div class="card" style="max-width:420px; padding:24px; width:90vw; border:1px solid var(--line);">
      <div class="row-between" style="margin-bottom:16px;">
        <div>
          <h2>{{ tr('联系客服', 'Contact Support') }}</h2>
          <div class="muted" style="font-size:12.5px; margin-top:3px;">{{ tr('24 小时内回复 (中文 / 英文)', 'Response within 24 hours (Chinese / English)') }}</div>
        </div>
        <button class="icon-btn" @click="app.contactModal = false">×</button>
      </div>

      <div style="display:flex; flex-direction:column; gap:10px; margin-bottom:14px;">
        <div class="row" style="padding:12px 14px; background:var(--panel-soft); border-radius:8px; gap:10px;">
          <svg width="18" height="18" fill="none" stroke="var(--text-dim)" stroke-width="1.8" viewBox="0 0 24 24" style="flex-shrink:0;"><path d="M21.5 4.5L2.5 12.4l6 2.3 2.3 6L21.5 4.5z"/></svg>
          <div style="flex:1; min-width:0;">
            <div style="font-weight:500; font-size:13px;">Telegram</div>
            <div class="mono faint" style="font-size:11.5px;">@ghostmap_cn</div>
          </div>
          <button class="btn btn-ghost btn-xs">{{ tr('打开', 'Open') }}</button>
        </div>
        <div class="row" style="padding:12px 14px; background:var(--panel-soft); border-radius:8px; gap:10px;">
          <svg width="18" height="18" fill="none" stroke="var(--text-dim)" stroke-width="1.8" viewBox="0 0 24 24" style="flex-shrink:0;"><path d="M4 4h16v16H4zM4 4l8 8 8-8"/></svg>
          <div style="flex:1; min-width:0;">
            <div style="font-weight:500; font-size:13px;">{{ tr('邮件', 'Email') }}</div>
            <div class="mono faint" style="font-size:11.5px;">support@ghostmap.cn</div>
          </div>
          <button class="btn btn-ghost btn-xs">{{ tr('复制', 'Copy') }}</button>
        </div>
        <div class="row" style="padding:12px 14px; background:var(--panel-soft); border-radius:8px; gap:10px;">
          <svg width="18" height="18" fill="none" stroke="var(--text-dim)" stroke-width="1.8" viewBox="0 0 24 24" style="flex-shrink:0;"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
          <div style="flex:1; min-width:0;">
            <div style="font-weight:500; font-size:13px;">{{ tr('驳回申诉', 'Rejected Submission Appeal') }}</div>
            <div class="faint" style="font-size:11.5px;">{{ tr('triager 错驳时, 协助您撰写申诉', 'Help you draft an appeal when a triager wrongly downgrades') }}</div>
          </div>
          <button class="btn btn-ghost btn-xs" @click="goRebuttalFromContact">{{ tr('进入', 'Open') }}</button>
        </div>
      </div>

      <button class="btn btn-ghost btn-sm" style="width:100%; justify-content:center;" @click="app.contactModal = false">{{ tr('关闭', 'Close') }}</button>
    </div>
  </div>

  <!-- Feedback Modal -->
  <div class="modal-mask" :class="{ shown: app.feedbackModal }" @click.self="app.feedbackModal = false">
    <div class="card" style="max-width:480px; padding:24px; width:90vw; border:1px solid var(--line);">
      <div class="row-between" style="margin-bottom:14px;">
        <div>
          <h2>{{ tr('提建议 / 提需求', 'Suggestions') }}</h2>
          <div class="muted" style="font-size:12.5px; margin-top:3px;">{{ tr('我们认真阅读每一条建议, 入选规划后将通过邮件回复', 'We read every suggestion carefully and reply by email when accepted into the roadmap') }}</div>
        </div>
        <button class="icon-btn" @click="app.feedbackModal = false">×</button>
      </div>
      <textarea :placeholder="tr('例如: 希望支持某类型扫描 / 报告导出新增格式 / 集成新的平台...', 'e.g. Support a new scan type / add export format / integrate a new platform...')" style="width:100%; min-height:120px; padding:10px 12px; background:var(--bg-alt); color:var(--text); border:1px solid var(--line); border-radius:6px; font-size:13px; font-family:inherit; resize:vertical; outline:none; margin-bottom:12px;" />
      <div class="row" style="gap:8px;">
        <button class="btn btn-primary btn-sm" style="flex:1; justify-content:center;">{{ tr('提交', 'Submit') }}</button>
        <button class="btn btn-ghost btn-sm" @click="app.feedbackModal = false">{{ tr('关闭', 'Close') }}</button>
      </div>
    </div>
  </div>

  <!-- Changelog Modal -->
  <div class="modal-mask" :class="{ shown: app.changelogModal }" @click.self="app.changelogModal = false">
    <div class="card" style="max-width:520px; padding:24px; width:90vw; border:1px solid var(--line);">
      <div class="row-between" style="margin-bottom:16px;">
        <div>
          <h2>{{ tr('更新日志', 'Changelog') }}</h2>
          <div class="muted" style="font-size:12.5px; margin-top:3px;">{{ tr('最近 3 个版本', 'Latest 3 versions') }}</div>
        </div>
        <button class="icon-btn" @click="app.changelogModal = false">×</button>
      </div>
      <div style="display:flex; flex-direction:column; gap:14px; margin-bottom:14px;">
        <div>
          <div class="row" style="gap:8px; margin-bottom:6px;">
            <span class="chip chip-soft" style="font-weight:600;">v0.1.0</span>
            <span class="faint" style="font-size:11.5px;">{{ tr('2026-06-15 · 首发', '2026-06-15 · Initial Release') }}</span>
          </div>
          <ul class="muted" style="font-size:12.5px; line-height:1.7; padding-left:18px; margin:0;">
            <li>{{ tr('资产侦察 + 安全检查 + 报告生成 全链路上线', 'Full pipeline live: asset recon + checks + report generation') }}</li>
            <li>{{ tr('Docker 一键启动 + Web UI', 'One-click Docker startup + Web UI') }}</li>
            <li>{{ tr('中英文双语 + 13 家 AI 厂商接入 (DeepSeek / 通义千问 / Claude / GPT 等)', 'Bilingual UI + 13 AI vendors (DeepSeek / Qwen / Claude / GPT, etc.)') }}</li>
          </ul>
        </div>
        <div>
          <div class="row" style="gap:8px; margin-bottom:6px;">
            <span class="chip chip-soft">v0.0.9</span>
            <span class="faint" style="font-size:11.5px;">{{ tr('2026-05-30 · 预览版', '2026-05-30 · Preview') }}</span>
          </div>
          <ul class="muted" style="font-size:12.5px; line-height:1.7; padding-left:18px; margin:0;">
            <li>{{ tr('资产侦察 + 漏洞扫描全链路演示版可跑', 'Asset recon + vulnerability scan demo pipeline runnable') }}</li>
            <li>{{ tr('反滥用机制 + 任务风险评分', 'Anti-abuse mechanism + task risk scoring') }}</li>
          </ul>
        </div>
        <div>
          <div class="row" style="gap:8px; margin-bottom:6px;">
            <span class="chip chip-soft">v0.0.5</span>
            <span class="faint" style="font-size:11.5px;">{{ tr('2026-05-17 · 初版骨架', '2026-05-17 · Initial Skeleton') }}</span>
          </div>
          <ul class="muted" style="font-size:12.5px; line-height:1.7; padding-left:18px; margin:0;">
            <li>{{ tr('后端服务初版上线', 'Initial backend services online') }}</li>
            <li>{{ tr('本地数据加密保存 · API Key 不出本机', 'Local data encrypted at rest · API Key stays on-device') }}</li>
          </ul>
        </div>
      </div>
      <div class="row" style="gap:8px;">
        <button class="btn btn-ghost btn-sm" style="flex:1; justify-content:center;">{{ tr('完整 CHANGELOG →', 'Full CHANGELOG →') }}</button>
        <button class="btn btn-ghost btn-sm" @click="app.changelogModal = false">{{ tr('关闭', 'Close') }}</button>
      </div>
    </div>
  </div>

  <!-- Report Preview Modal -->
  <div class="modal-mask" :class="{ shown: app.reportPreviewModal }" @click.self="app.reportPreviewModal = false">
    <div class="card" style="max-width:780px; width:92vw; max-height:88vh; padding:0; display:flex; flex-direction:column; border:1px solid var(--line);">
      <div class="row-between" style="padding:18px 22px; border-bottom:1px solid var(--line-soft); flex-shrink:0;">
        <div style="min-width:0; flex:1;">
          <div class="chip chip-soft" style="margin-bottom:6px;">{{ tr('已生成', 'Generated') }}</div>
          <h2 style="margin:0; overflow:hidden; text-overflow:ellipsis;">{{ previewTitle }}</h2>
          <div class="muted" style="font-size:12px; margin-top:3px;">{{ tr('按目标平台模板规范生成 · 字段顺序与必填项已严格对齐 · 内容可直接粘贴至平台提交框', 'Generated per target-platform template spec · field order and required items strictly aligned · paste directly into the platform submission form') }}</div>
        </div>
        <button class="icon-btn" @click="app.reportPreviewModal = false">×</button>
      </div>

      <div style="flex:1; overflow-y:auto; padding:0;">
        <pre class="mono" style="margin:0; padding:18px 22px; font-size:12.5px; line-height:1.65; background:var(--bg-alt); color:var(--text); white-space:pre-wrap; word-wrap:break-word; border:none; border-radius:0;">{{ previewMarkdown }}</pre>
      </div>

      <div class="row-between" style="padding:14px 22px; border-top:1px solid var(--line-soft); flex-shrink:0;">
        <div class="faint" style="font-size:11.5px;">{{ previewSummary || tr('在 Reports → 报告模板 页面点「生成报告」', "Use Reports → Templates → 'Generate Report'") }}</div>
        <div class="row" style="gap:6px;">
          <button class="btn btn-ghost btn-sm" :disabled="!app.generatedReport" @click="downloadReport('md')">{{ tr('下载 .md', 'Download .md') }}</button>
          <button class="btn btn-ghost btn-sm" :disabled="!app.generatedReport" @click="downloadReport('json')">{{ tr('下载 .json', 'Download .json') }}</button>
          <button class="btn btn-primary btn-sm" :disabled="!app.generatedReport" @click="copyReport">{{ tr('复制全文 · 直接粘贴提交', 'Copy All · paste to submit') }}</button>
        </div>
      </div>
    </div>
  </div>

  <!-- New Scan Modal -->
  <div class="modal-mask" :class="{ shown: app.newScanModal }" @click.self="app.newScanModal = false">
    <div class="card" style="max-width:520px; padding:24px; width:90vw; border:1px solid var(--line);">
      <div class="row-between" style="margin-bottom:16px;">
        <div>
          <h2>{{ tr('新建扫描', 'New Scan') }}</h2>
          <div class="muted" style="font-size:12.5px; margin-top:3px;">
            {{ tr('选择目标 + 预设,Worker 会被动跑公开 OSINT 子域枚举,完成后子域出现在「资产」清单。', 'Pick a target + preset; the worker runs passive OSINT subdomain enumeration. Discovered hosts will appear in the Asset Inventory when done.') }}
          </div>
        </div>
        <button class="icon-btn" @click="app.newScanModal = false">×</button>
      </div>

      <div style="margin-bottom:14px;">
        <label class="field">{{ tr('目标', 'Target') }}</label>
        <select v-model.number="scanFormTargetId">
          <option :value="null" disabled>{{ tr('— 选一个目标 —', '— Pick a target —') }}</option>
          <option v-for="t in res.viewTargets" :key="t.id" :value="t.id">
            {{ (locale === 'en' ? (t.nameEn || t.name) : t.name) }} — {{ t.scope }}
          </option>
        </select>
      </div>

      <div style="margin-bottom:14px;">
        <label class="field">{{ tr('扫描预设', 'Scan Preset') }}</label>
        <select v-model="scanFormPreset">
          <option value="public_recon">{{ tr('公开侦察 — 纯被动 OSINT (推荐 / 已接通)', 'Public Recon — passive OSINT (recommended / live)') }}</option>
          <option value="standard_sweep">{{ tr('标准巡检 — 主动探活 (开发中,目前回退至公开侦察)', 'Standard Sweep — active probing (in development, falls back to Public Recon for now)') }}</option>
          <option value="deep_audit">{{ tr('深度审计 — 全模板检查 (开发中,目前回退至公开侦察)', 'Deep Audit — full template checks (in development, falls back to Public Recon for now)') }}</option>
        </select>
        <div class="faint" style="font-size:11.5px; margin-top:6px;">
          {{ tr('Public Recon 仅查询 crt.sh / wayback 等公开数据源,不向目标资产发送任何请求。', 'Public Recon only queries crt.sh / wayback and other public sources; no traffic to the target asset.') }}
        </div>
      </div>

      <div v-if="scanFormError" class="card" style="margin-bottom:14px; padding:10px 12px; background:rgba(196,77,77,0.08); border:1px solid rgba(196,77,77,0.3); font-size:12px; color:var(--sev-crit);">
        {{ scanFormError }}
      </div>
      <div v-if="scanFormSuccessMsg" class="card" style="margin-bottom:14px; padding:10px 12px; background:rgba(77,155,110,0.08); border:1px solid rgba(77,155,110,0.3); font-size:12px; color:var(--sev-low);">
        {{ scanFormSuccessMsg }}
      </div>

      <div class="row" style="gap:8px;">
        <button class="btn btn-primary btn-sm" style="flex:1; justify-content:center;" :disabled="scanFormSubmitting" @click="submitScan">
          <span v-if="!scanFormSubmitting">{{ tr('启动扫描', 'Start Scan') }}</span>
          <span v-else>{{ tr('提交中…', 'Submitting…') }}</span>
        </button>
        <button class="btn btn-ghost btn-sm" @click="app.newScanModal = false">{{ tr('关闭', 'Close') }}</button>
      </div>
    </div>
  </div>

</template>
